import {
  NetworkStatus,
  useLazyQuery,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@apollo/client";
import {
  assetBalanceTransactionTypeToDisplayName,
  Button,
  ControlledDateFilterControl,
  ControlledDateFilterSummary,
  ControlledDecimalFilterControl,
  ControlledDecimalFilterSummary,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DirectionIcon,
  FXTradeCard,
  getAccountDisplayFields,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Number,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TransactionStatusUI,
  Txt,
  toast,
  transactionTypeToAssetBalanceTransactionType,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import {
  decimalSchema,
  downloadString,
  formatLocalDateComponents,
  neverFunc,
  prettyPrintEnum,
} from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { Suspense, useMemo, useRef, useState } from "react";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { PiBuildingOffice } from "react-icons/pi";
import {
  RiBankLine,
  RiBuilding2Line,
  RiCalendarLine,
  RiDownload2Line,
  RiFilter3Line,
  RiNumbersLine,
  RiPlaneLine,
  RiPriceTagLine,
  RiShoppingBasket2Line,
  RiWalletLine,
} from "react-icons/ri";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { FundMovementOptionsDialog } from "~/components/ledger-transaction-detail/FundMovementOptionsDialog";
import { graphql } from "~/gql/client";
import {
  transactionPageOrderValues,
  transactionStatusValues,
  transactionTypeValues,
} from "~/gql/client/enumValues";
import type {
  AdminTransactionRowFragment,
  TransactionPageFilter,
  TransactionPageOrder,
  TransactionStatus,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/transactions")({
  head: () => opsHead("Transactions"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { useSearchParams } = useRouter();

  const [triggerCron] = useMutation(
    graphql(`
      mutation TransactionsPageTriggerCron($input: TriggerCronInput!) {
        triggerCron(input: $input) {
          jobId
          taskId
        }
      }
    `),
  );

  const handleTriggerCron = async (taskId: string) => {
    try {
      await triggerCron({ variables: { input: { taskId } } });
      toast({
        title: `${taskId} triggered`,
        kind: "success",
        autoHide: true,
      });
    } catch (error) {
      toast({
        kind: "error",
        autoHide: false,
        title: "Failed to trigger",
        description: String(error),
      });
    }
  };

  graphql(`
    fragment AdminTransactionRow on Transaction {
      id
      createdAt
      type
      direction
      currencyAmount {
        currency
        amount
      }
      effectiveAt
      status
      description
      reference
      unitAmount {
        unit
        amount
      }
      ledgerAccount {
        ... on AssetLedgerAccount {
          asset {
            ...AssetCardFields
            businessEntity {
              id
              name
              organisation {
                id
                name
                squareLogoUrl
              }
            }
          }
        }
      }
      fundMovement {
        ... on Deposit {
          id
          destinationAsset {
            ...AssetCardFields
            businessEntity {
              id
              name
              organisation {
                id
                name
                squareLogoUrl
              }
            }
          }
        }
        ... on Transfer {
          id
          sourceAsset {
            ...AssetCardFields
          }
          destinationAccount {
            ...AssetCardFields
            ...BeneficiaryAssetCardFields
          }
          paymentReference
        }
        ... on EburyFXTrade {
          id
          ...FXTradeCardFields
        }
      }
    }
  `);

  const transactionsDocument = graphql(`
    query AdminTransactions($after: String, $first: Int!, $order: TransactionPageOrder, $filter: TransactionPageFilter) {
      transactions(after: $after, first: $first, order: $order, filter: $filter) {
        pageInfo {
          endCursor
          hasNextPage
        }
        transactions {
          ...AdminTransactionRow
        }
      }
    }
  `);

  const assetsDocument = graphql(`
    query AdminTransactionPage {
      custodyAccounts {
        id
        shortRef
        product {
          id
          internalName
        }
      }
      providers {
        id
        internalName
        name
      }
      products {
        id
        internalName
      }
      organisations {
        id
        name
        businessEntities {
          id
          name
          assets(includeInactive: true) {
            id
            ...AssetCardFields
          }
        }
      }
    }
  `);

  const [filterAndSortState, setFilterAndSortOrder] =
    useQueryPersistedStateWithSchema({
      useSearchParams,
      schema: z.object({
        assetIds: z.array(z.string()).optional().nullable(),
        endDateExclusive: z.date().optional().nullable(),
        excludeExternalAccounts: z.boolean().optional().nullable(),
        organisationIds: z.array(z.string()).optional().nullable(),
        businessEntityIds: z.array(z.string()).optional().nullable(),
        custodyAccountIds: z.array(z.string()).optional().nullable(),
        productIds: z.array(z.string()).optional().nullable(),
        providerIds: z.array(z.string()).optional().nullable(),
        startDateInclusive: z.date().optional().nullable(),
        status: z.array(z.enum(transactionStatusValues)).optional().nullable(),
        transactionType: z
          .array(z.enum(transactionTypeValues))
          .optional()
          .nullable(),
        sortOrder: z.enum(transactionPageOrderValues).optional().nullable(),
        inflight: z.boolean().optional().nullable(),
        amount: z
          .object({
            gte: decimalSchema.optional().nullable(),
            lt: decimalSchema.optional().nullable(),
          })
          .optional()
          .nullable(),
      }),
      defaultState: {
        sortOrder: "EFFECTIVE_AT_DESC",
        excludeExternalAccounts: true,
      },
    });

  const { sortOrder, ...filter } = filterAndSortState;

  const baseInput = {
    first: 60,
    order: sortOrder,
    filter,
  };

  const [getTransactionsCsv, { loading: downloadCsvLoading }] = useLazyQuery(
    graphql(`
      query AdminTransactionsCsv($order: TransactionPageOrder, $filter: TransactionPageFilter) {
        transactions(first: 0, order: $order, filter: $filter) {
          fullCsvContents
        }
      }
    `),
  );

  const downloadCsv = async () => {
    await getTransactionsCsv({
      variables: {
        filter: baseInput.filter ?? null,
        order: baseInput.order,
      },
      onCompleted: (data) => {
        const csv = data?.transactions?.fullCsvContents;
        if (csv) {
          downloadString(
            csv,
            `Primary transactions ${new Date().toISOString()}.csv`,
          );
        }
      },
      onError: () => {
        toast({
          kind: "error",
          autoHide: false,
          title: "Something went wrong fetching transactions",
        });
      },
      fetchPolicy: "no-cache",
    });
  };

  const { fetchMore, loading, networkStatus, data } = useQuery(
    transactionsDocument,
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const transactions = data?.transactions?.transactions ?? [];
  const nextCursor = data?.transactions?.pageInfo.hasNextPage
    ? (data.transactions.pageInfo.endCursor ?? undefined)
    : undefined;

  const { data: pageData } = useSuspenseQuery(assetsDocument);

  const handleSortChange = (newSortingState: SortingState) => {
    const primarySort = newSortingState.at(0);
    let newSortOrder: TransactionPageOrder;
    if (primarySort?.id === "date") {
      newSortOrder = primarySort.desc
        ? "EFFECTIVE_AT_DESC"
        : "EFFECTIVE_AT_ASC";
    } else if (primarySort?.id === "amount") {
      newSortOrder = primarySort.desc ? "AMOUNT_DESC" : "AMOUNT_ASC";
    } else {
      newSortOrder = "EFFECTIVE_AT_DESC";
    }
    if (newSortOrder !== sortOrder) {
      setFilterAndSortOrder({
        ...filterAndSortState,
        sortOrder: newSortOrder,
      });
    }
  };

  const productOptions =
    pageData.products?.flatMap((p) => ({
      value: p.id,
      label: p.internalName,
    })) ?? [];

  const providerOptions =
    pageData.providers?.flatMap((p) => ({
      value: p.id,
      searchValues: [p.name, p.internalName],
      label: (
        <div>
          {p.name}
          {p.internalName !== p.name ? ` (${p.internalName})` : ""}
        </div>
      ),
    })) ?? [];

  const custodyAccountOptions =
    pageData.custodyAccounts?.flatMap((custodyAccount) => ({
      value: custodyAccount.id,
      searchValues: [
        custodyAccount.shortRef,
        custodyAccount.product?.internalName,
      ].filter((item): item is string => !!item),
      label: custodyAccount.product?.internalName ?? "Unknown",
    })) ?? [];

  const assetOptions =
    pageData.organisations?.flatMap(
      (o) =>
        o.businessEntities?.flatMap(
          (be) =>
            be.assets?.map((a) => {
              const { businessEntityName, compactName } =
                getAccountDisplayFields({ account: a });
              return {
                value: a.id,
                searchValues: [a.name],
                label: `${compactName} (${businessEntityName})`,
                orgId: o.id,
              };
            }) ?? [],
        ) ?? [],
    ) ?? [];

  const organisationOptions =
    pageData.organisations?.flatMap((o) => ({
      value: o.id,
      label: o.name,
    })) ?? [];

  const businessEntityOptions = (pageData.organisations ?? [])
    .filter((org) => {
      if (filter.organisationIds && filter.organisationIds?.length > 0) {
        return filter.organisationIds.includes(org.id);
      }
      return true;
    })
    .flatMap((org) =>
      (org.businessEntities ?? []).map((be) => {
        return {
          value: be.id,
          searchValues: [be.name],
          label: (
            <div>
              {be.name} ({org.name})
            </div>
          ),
        };
      }),
    );

  const filterAndSortStateRef = useRef(filterAndSortState);
  filterAndSortStateRef.current = filterAndSortState;

  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | undefined
  >(undefined);

  const columns = useMemo<ColumnDef<AdminTransactionRowFragment>[]>(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        cell: () => null,
      },
      {
        id: "status",
        accessorKey: "status",
        header: "",
        cell: ({ getValue }) => (
          <TransactionStatusUI
            iconOnly
            status={getValue<TransactionStatus>()}
          />
        ),
      },
      {
        id: "date",
        accessorFn: () => undefined,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Date" />
        ),
        cell: ({ row }) => {
          const { shortDate, shortTime } = formatLocalDateComponents(
            row.original.effectiveAt,
          );

          return (
            <Tooltip>
              <TooltipTrigger>
                <div className="min-w-24">
                  <Txt>{shortDate}</Txt>
                </div>
              </TooltipTrigger>
              <TooltipContent className="border bg-white p-2">
                <Txt>{shortDate}</Txt>
                <Txt variant="caption">{shortTime}</Txt>
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        id: "business_entity",
        header: "Business Entity",
        cell: ({ row }) => {
          let beName: string | undefined = undefined;
          let beId: string | undefined = undefined;
          let orgName: string | undefined = undefined;
          let orgId: string | undefined = undefined;
          let logoUrl: string | undefined = undefined;

          if (
            row.original.ledgerAccount?.__typename === "AssetLedgerAccount" &&
            row.original.ledgerAccount.asset?.businessEntity
          ) {
            const be = row.original.ledgerAccount.asset.businessEntity;
            beName = be.name;
            beId = be.id;
            orgName = be.organisation?.name ?? "";
            orgId = be.organisation?.id;
            logoUrl = be.organisation?.squareLogoUrl ?? "";
          }
          return (
            <HoverCard openDelay={25}>
              <HoverCardTrigger asChild>
                <div className="flex max-w-48 cursor-default flex-row items-center gap-0">
                  {logoUrl ? (
                    <img
                      className="mr-2 size-6 shrink-0 rounded-sm"
                      src={logoUrl}
                      alt="Business entity logo"
                    />
                  ) : (
                    <PiBuildingOffice className="mr-2 shrink-0 text-sm text-tertiary" />
                  )}
                  <Txt truncate>{beName}</Txt>
                </div>
              </HoverCardTrigger>
              <HoverCardContent
                padding="sm"
                className="min-w-48 hover:cursor-default"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <div className="mb-2 flex flex-row items-center gap-1 p-1">
                  {logoUrl ? (
                    <img
                      className="mr-2 size-6 shrink-0 rounded-sm"
                      src={logoUrl}
                      alt="Business entity logo"
                    />
                  ) : (
                    <PiBuildingOffice className="mr-2 shrink-0 text-sm text-tertiary" />
                  )}
                  <div className="flex min-w-0 flex-col">
                    <Txt>{beName}</Txt>
                    <Txt variant="caption">{orgName}</Txt>
                  </div>
                </div>
                {beId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-full justify-start rounded-xs text-xs"
                    onClick={() => {
                      if (!beId) {
                        return;
                      }
                      setFilterAndSortOrder({
                        ...filterAndSortStateRef.current,
                        businessEntityIds: [beId],
                        organisationIds: undefined,
                        assetIds: undefined,
                      });
                    }}
                  >
                    Filter by this entity
                  </Button>
                )}
                {orgId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-full justify-start rounded-xs text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (!orgId) {
                        return;
                      }
                      setFilterAndSortOrder({
                        ...filterAndSortStateRef.current,
                        organisationIds: [orgId],
                        businessEntityIds: undefined,
                        assetIds: undefined,
                      });
                    }}
                  >
                    Filter by this organisation
                  </Button>
                )}
              </HoverCardContent>
            </HoverCard>
          );
        },
      },
      {
        id: "account",
        header: "Account",
        cell: ({ row }) => {
          const ledgerAccount = row.original.ledgerAccount;
          if (
            !ledgerAccount ||
            ledgerAccount.__typename !== "AssetLedgerAccount" ||
            !ledgerAccount.asset
          ) {
            return null;
          }
          const asset = ledgerAccount.asset;
          const {
            logoUrl,
            friendlyIdentifier,
            compactProviderName,
            compactName,
          } = getAccountDisplayFields({ account: asset });
          return (
            <HoverCard openDelay={25}>
              <HoverCardTrigger asChild>
                <div className="flex max-w-[24rem] cursor-default items-center gap-1">
                  <img
                    src={logoUrl}
                    className="inline h-[1.5em] w-[1.5em] rounded-sm"
                  />
                  <div className="max-w-48 text-left">
                    <Txt truncate>{compactName}</Txt>
                  </div>
                </div>
              </HoverCardTrigger>
              <HoverCardContent
                padding="sm"
                className="min-w-48 hover:cursor-default"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <div className="mb-2 flex items-center gap-1 p-1">
                  <img
                    src={logoUrl}
                    className="inline h-[1.5em] w-[1.5em] rounded-sm"
                  />
                  <div>
                    <Txt>
                      {compactProviderName} {compactName}
                    </Txt>
                    <Txt variant="caption">{friendlyIdentifier}</Txt>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-full justify-start rounded-xs text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setFilterAndSortOrder({
                      ...filterAndSortStateRef.current,
                      assetIds: [asset.id],
                      excludeExternalAccounts: false,
                    });
                  }}
                >
                  Filter by this account
                </Button>
              </HoverCardContent>
            </HoverCard>
          );
        },
      },
      {
        id: "description",
        header: "Description",
        cell: ({ row }) => {
          return (
            <Tooltip>
              <TooltipTrigger>
                <div className="max-w-64">
                  <Txt truncate>{row.original.description}</Txt>
                </div>
              </TooltipTrigger>
              <TooltipContent className="border bg-white p-2">
                <Txt truncate>{row.original.description}</Txt>
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        id: "amount",
        accessorFn: () => undefined,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title="Amount"
            align="right"
            className="flex flex-row justify-end pr-2"
          />
        ),
        cell: ({ row }) => {
          const { amount, currency } = row.original.currencyAmount;

          return (
            <div className="flex w-full flex-row justify-end gap-2">
              {row.original.unitAmount && (
                <Tooltip>
                  <TooltipTrigger>
                    <HiOutlineInformationCircle />
                  </TooltipTrigger>
                  <TooltipContent>
                    <Number
                      value={row.original.unitAmount.amount.mul(
                        row.original.direction === "DEBIT" ? -1 : 1,
                      )}
                      format={Number.format.units(
                        row.original.unitAmount.amount.mul(
                          row.original.direction === "DEBIT" ? -1 : 1,
                        ),
                      )}
                    />
                  </TooltipContent>
                </Tooltip>
              )}

              <span className="flex flex-row justify-end gap-1">
                <CurrencyAmountUI
                  amount={amount.mul(
                    row.original.direction === "DEBIT" ? -1 : 1,
                  )}
                  currency={currency}
                  showSymbol={false}
                />
                <DirectionIcon direction={row.original.direction} />
              </span>
            </div>
          );
        },
      },
      {
        id: "fund_movement",
        accessorFn: () => undefined,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title="Fund movement"
            align="left"
            className="flex flex-row justify-end pr-2"
          />
        ),
        cell: ({ row }) => {
          switch (row.original.type) {
            case "TRANSFER_IN":
              if (
                row.original.fundMovement?.__typename === "Transfer" &&
                row.original.fundMovement.sourceAsset
              ) {
                const {
                  logoUrl,
                  compactProviderName,
                  compactName,
                  friendlyIdentifier,
                } = getAccountDisplayFields({
                  account: row.original.fundMovement.sourceAsset,
                });
                return (
                  <HoverCard openDelay={25}>
                    <HoverCardTrigger>
                      <Txt>
                        Transfer {row.original.fundMovement.paymentReference}
                      </Txt>
                    </HoverCardTrigger>
                    <HoverCardContent
                      className="flex flex-col gap-2 p-2 hover:cursor-default"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      <Txt variant="caption">Transfer in from</Txt>
                      <div className="flex items-center gap-2">
                        <img
                          src={logoUrl}
                          className="inline h-[1.5em] w-[1.5em] rounded-sm"
                        />
                        <div>
                          <Txt>
                            {compactProviderName} {compactName}
                          </Txt>
                          <Txt variant="mono">{friendlyIdentifier}</Txt>
                        </div>
                      </div>

                      <Link
                        to="/transfers/$id"
                        params={{ id: row.original.fundMovement.id }}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-full justify-start rounded-xs text-xs"
                        >
                          View transfer
                        </Button>
                      </Link>
                    </HoverCardContent>
                  </HoverCard>
                );
              }
              return "Transfer In";
            case "TRANSFER_OUT":
              if (
                row.original.fundMovement?.__typename === "Transfer" &&
                row.original.fundMovement.destinationAccount
              ) {
                const {
                  logoUrl,
                  compactProviderName,
                  compactName,
                  friendlyIdentifier,
                } = getAccountDisplayFields({
                  account: row.original.fundMovement.destinationAccount,
                });

                const actionLabel =
                  row.original.fundMovement.destinationAccount.__typename ===
                  "BeneficiaryAccount"
                    ? "Payout"
                    : "Transfer";

                return (
                  <HoverCard openDelay={25}>
                    <HoverCardTrigger>
                      <Txt>
                        {actionLabel}{" "}
                        {row.original.fundMovement.paymentReference}
                      </Txt>
                    </HoverCardTrigger>
                    <HoverCardContent
                      className="flex flex-col gap-2 p-2 hover:cursor-default"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      <Txt variant="caption">{actionLabel} to</Txt>
                      <div className="flex items-center gap-2">
                        <img
                          src={logoUrl}
                          className="inline h-[1.5em] w-[1.5em] rounded-sm"
                        />
                        <div>
                          <Txt>
                            {compactProviderName} {compactName}
                          </Txt>
                          <Txt variant="mono">{friendlyIdentifier}</Txt>
                        </div>
                      </div>

                      <Link
                        to="/transfers/$id"
                        params={{ id: row.original.fundMovement.id }}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-full justify-start rounded-xs text-xs"
                        >
                          View transfer
                        </Button>
                      </Link>
                    </HoverCardContent>
                  </HoverCard>
                );
              }
              return "Transfer out";
            case "MARGIN":
              const ledgerAccount = row.original.ledgerAccount;
              if (
                row.original.fundMovement?.__typename === "EburyFXTrade" &&
                ledgerAccount &&
                ledgerAccount.__typename === "AssetLedgerAccount" &&
                ledgerAccount.asset
              ) {
                return (
                  <HoverCard openDelay={25}>
                    <HoverCardTrigger>
                      <Txt>FX Trade (margin)</Txt>
                    </HoverCardTrigger>
                    <HoverCardContent
                      className="flex flex-col gap-2 p-2 hover:cursor-default"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      <FXTradeCard fxTrade={row.original.fundMovement} />
                      <Link
                        to="/fx-trades/$id"
                        params={{ id: row.original.fundMovement.id }}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-full justify-start rounded-xs text-xs"
                        >
                          View FX trade
                        </Button>
                      </Link>
                    </HoverCardContent>
                  </HoverCard>
                );
              }
              return "FX Margin";
            case "UNCATEGORISED":
              return (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setSelectedTransactionId(row.original.id);
                  }}
                >
                  Categorise
                </Button>
              );
            case "BALANCE_SYNC":
            case "PROVIDER_FEE":
            case "STATEMENT_FEE":
            case "STRIPE_PAYOUT":
            case "YIELD_REMITTANCE":
            case "DIRECT_WITHDRAWAL":
            case "YIELD_REINVESTMENT":
            case "DEPOSIT":
            case "TAX":
            case "YIELD":
            case "FUND_TRADE":
            case "PAYOUT":
              return prettyPrintEnum(row.original.type);
            case null:
            case undefined:
              return null;
            default:
              neverFunc(row.original.type);
          }
        },
      },
    ],
    [setFilterAndSortOrder],
  );

  return (
    <AdminPageContainer testId="dashboard-transactions">
      <AdminPageHeader
        title="Transactions"
        subtitle={"Changes to your account balances"}
        buttons={
          <div className="flex flex-row gap-4">
            <Link to="/transactions/batch-import">
              <Button>Batch import</Button>
            </Link>
            <Button
              variant="outline"
              onClick={() =>
                void handleTriggerCron("initiateMacquarieAccountsSyncCronTask")
              }
            >
              Sync Macquarie
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                void handleTriggerCron(
                  "initiateReconcileMacquarieTransactionsCronTask",
                )
              }
            >
              Rec Macquarie
            </Button>
            <Link to="/transactions/new">
              <Button variant="primary">New Transaction</Button>
            </Link>
          </div>
        }
      />
      <FilterBar
        filterValue={filter}
        onFilterChange={(values) => {
          setFilterAndSortOrder({
            ...values,
            sortOrder,
          });
        }}
        assetOptions={assetOptions}
        productOptions={productOptions}
        organisationOptions={organisationOptions}
        businessEntityOptions={businessEntityOptions}
        providerOptions={providerOptions}
        custodyAccountOptions={custodyAccountOptions}
      />
      <DataTable
        data={transactions}
        columns={columns}
        manualSorting
        manualFiltering
        isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
        manualPagination
        maxMultiSortColCount={1}
        hidePagination
        pageSize={10_000}
        className="overflow-hidden border"
        cellClassName="p-2"
        linkTo={(row) => {
          return `/transactions/${row.original.id}`;
        }}
        onSortingChange={handleSortChange}
        columnSortProps={{
          id: "date",
          desc: true,
        }}
        initialColumnVisibility={{
          id: false,
        }}
        showCsvExport={false}
      />
      <FundMovementOptionsDialog
        transactionId={selectedTransactionId}
        onClose={() => setSelectedTransactionId(undefined)}
      />
      <div className="mt-4 flex w-full flex-row items-center justify-center gap-4">
        {nextCursor ? (
          <Button
            onClick={() =>
              fetchMore({
                variables: {
                  after: nextCursor,
                  ...baseInput,
                },
              })
            }
            variant="outline"
            loading={loading || networkStatus === NetworkStatus.fetchMore}
          >
            Load more
          </Button>
        ) : (
          !!transactions.length && (
            <p className="text-secondary text-sm">No more data</p>
          )
        )}
        {!!transactions.length && (
          <Button
            variant="outline"
            leftIcon={<RiDownload2Line />}
            onClick={() => void downloadCsv()}
            disabled={downloadCsvLoading}
            loading={downloadCsvLoading}
          >
            Export
          </Button>
        )}
      </div>
    </AdminPageContainer>
  );
}

function FilterBar({
  filterValue,
  onFilterChange,
  assetOptions,
  organisationOptions,
  businessEntityOptions,
  productOptions,
  providerOptions,
  custodyAccountOptions,
}: {
  filterValue: TransactionPageFilter;
  onFilterChange: (_: TransactionPageFilter) => void;
  assetOptions: {
    value: string;
    label: ReactNode;
    orgId: string;
  }[];
  organisationOptions: {
    value: string;
    label: ReactNode;
  }[];
  businessEntityOptions: {
    value: string;
    label: ReactNode;
  }[];
  productOptions: {
    value: string;
    label: ReactNode;
  }[];
  providerOptions: {
    value: string;
    label: ReactNode;
  }[];
  custodyAccountOptions: {
    value: string;
    label: ReactNode;
    searchValues?: string[];
  }[];
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("date");
  const filterCount =
    0 +
    ((filterValue?.status?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.assetIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.organisationIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.businessEntityIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.productIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.transactionType?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.providerIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.custodyAccountIds?.length ?? 0) > 0 ? 1 : 0) +
    (filterValue.startDateInclusive || filterValue.endDateExclusive ? 1 : 0) +
    (filterValue.amount?.gte || filterValue.amount?.lt ? 1 : 0) +
    (filterValue.excludeExternalAccounts ? 1 : 0) +
    (filterValue.inflight ? 1 : 0);

  const resetFilter = () => {
    onFilterChange({
      ...filterValue,
      startDateInclusive: undefined,
      endDateExclusive: undefined,
      organisationIds: undefined,
      businessEntityIds: undefined,
      status: undefined,
      assetIds: undefined,
      productIds: undefined,
      providerIds: undefined,
      custodyAccountIds: undefined,
      transactionType: undefined,
      amount: undefined,
      inflight: undefined,
      excludeExternalAccounts: undefined,
    });
  };

  const statusOptions = [
    {
      value: "POSTED" as const,
      label: <span>Posted</span>,
    },
    {
      value: "PENDING" as const,
      label: <span>Pending</span>,
    },
  ];

  const typeOptions = transactionTypeValues.map((k) => ({
    value: k,
    label: (
      <span>
        {
          assetBalanceTransactionTypeToDisplayName[
            transactionTypeToAssetBalanceTransactionType[k]
          ]
        }
      </span>
    ),
  }));

  return (
    <div className="flex w-full flex-row gap-2">
      <Popover open={open} onOpenChange={(open) => setOpen(open)}>
        <PopoverTrigger asChild>
          <Button
            leftIcon={<RiFilter3Line />}
            variant="outline"
            size="sm"
            className="mb-4 h-8 w-fit text-primary"
          >
            Filter{" "}
            {filterCount > 0 && (
              <span className="ml-2">
                <span className="text-info">({filterCount})</span>
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex h-[500px] w-[760px] p-0" align="start">
          <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("date")}
              >
                <RiCalendarLine />
                <div className="flex flex-1">Date</div>
                {(filterValue.endDateExclusive ||
                  filterValue.startDateInclusive) && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("status")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Status</div>
                {(filterValue.status?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("org")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Organisation</div>
                {(filterValue.organisationIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("business_entity")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Business Entities</div>
                {(filterValue.businessEntityIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("account")}
              >
                <RiWalletLine />
                <div className="flex flex-1">Account</div>
                {(filterValue.assetIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("product")}
              >
                <RiShoppingBasket2Line />
                <div className="flex flex-1">Product</div>
                {(filterValue.productIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("provider")}
              >
                <RiBankLine />
                <div className="flex flex-1">Provider</div>
                {(filterValue.providerIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("custody_account")}
              >
                <RiWalletLine />
                <div className="flex flex-1">Custody Account</div>
                {(filterValue.custodyAccountIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("type")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Type</div>
                {(filterValue.transactionType?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("amount")}
              >
                <RiNumbersLine />
                <div className="flex flex-1">Amount</div>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("inflight")}
              >
                <RiPlaneLine />
                <div className="flex flex-1">Inflight</div>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("external")}
              >
                <RiPlaneLine />
                <div className="flex flex-1">External accounts</div>
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-primary text-primary"
              onClick={resetFilter}
            >
              Reset all filters
            </Button>
          </div>
          <div className="flex-1">
            {path === "date" && (
              <ControlledDateFilterControl
                onChange={(value) => {
                  onFilterChange({
                    ...filterValue,
                    startDateInclusive: value?.startAtInclusive,
                    endDateExclusive: value?.endAtExclusive,
                  });
                }}
                filterValue={{
                  startAtInclusive: filterValue.startDateInclusive ?? undefined,
                  endAtExclusive: filterValue.endDateExclusive ?? undefined,
                }}
              />
            )}
            {path === "status" && (
              <ControlledFacetedFilterControl
                options={statusOptions}
                selectedValues={filterValue.status ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    status: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "type" && (
              <ControlledFacetedFilterControl
                options={typeOptions}
                selectedValues={filterValue.transactionType ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    transactionType: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "amount" && (
              <ControlledDecimalFilterControl
                onChange={(value) => {
                  onFilterChange({
                    ...filterValue,
                    amount: value,
                  });
                }}
                filterValue={{
                  gte: filterValue.amount?.gte ?? undefined,
                  lt: filterValue.amount?.lt ?? undefined,
                }}
              />
            )}
            {path === "inflight" && (
              <ControlledFacetedFilterControl
                options={[{ value: true, label: "Yes" }]}
                selectedValues={filterValue.inflight ? [true] : []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    inflight: newSelectedValues[0],
                  });
                }}
              />
            )}
            {path === "external" && (
              <ControlledFacetedFilterControl
                options={[{ value: true, label: "Exclude" }]}
                selectedValues={
                  filterValue.excludeExternalAccounts ? [true] : []
                }
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    excludeExternalAccounts: newSelectedValues[0],
                  });
                }}
              />
            )}
            {path === "account" && (
              <ControlledFacetedFilterControl
                options={
                  filterValue.organisationIds &&
                  filterValue.organisationIds.length
                    ? assetOptions.filter((a) =>
                        filterValue.organisationIds?.includes(a.orgId),
                      )
                    : assetOptions
                }
                selectedValues={filterValue.assetIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    assetIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "product" && (
              <ControlledFacetedFilterControl
                options={productOptions}
                selectedValues={filterValue.productIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    productIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "provider" && (
              <ControlledFacetedFilterControl
                options={providerOptions}
                selectedValues={filterValue.providerIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    providerIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "custody_account" && (
              <ControlledFacetedFilterControl
                options={custodyAccountOptions}
                selectedValues={filterValue.custodyAccountIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    custodyAccountIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "org" && (
              <ControlledFacetedFilterControl
                options={organisationOptions}
                selectedValues={filterValue.organisationIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    organisationIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                    // business entity options are narrowed depends on the org - reset when changing org
                    businessEntityIds: undefined,
                  });
                }}
              />
            )}
            {path === "business_entity" && (
              <ControlledFacetedFilterControl
                options={businessEntityOptions}
                selectedValues={filterValue.businessEntityIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    businessEntityIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>

      <ControlledDateFilterSummary
        onClick={() => {
          setPath("date");
          setOpen(true);
        }}
        value={{
          startAtInclusive: filterValue.startDateInclusive ?? undefined,
          endAtExclusive: filterValue.endDateExclusive ?? undefined,
        }}
      />

      <ControlledFactedFilterSummary
        title="Status"
        options={statusOptions}
        selectedValues={filterValue.status ?? []}
        onClick={() => {
          setPath("status");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Type"
        options={typeOptions}
        selectedValues={filterValue.transactionType ?? []}
        onClick={() => {
          setPath("type");
          setOpen(true);
        }}
      />

      <ControlledDecimalFilterSummary
        title="Amount"
        onClick={() => {
          setPath("amount");
          setOpen(true);
        }}
        value={{
          gte: filterValue.amount?.gte ?? undefined,
          lt: filterValue.amount?.lt ?? undefined,
        }}
      />

      <ControlledFactedFilterSummary
        title="Organisation"
        options={organisationOptions}
        selectedValues={filterValue.organisationIds ?? []}
        onClick={() => {
          setPath("org");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Business Entity"
        options={businessEntityOptions}
        selectedValues={filterValue.businessEntityIds ?? []}
        onClick={() => {
          setPath("business_entity");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Product"
        options={productOptions}
        selectedValues={filterValue.productIds ?? []}
        onClick={() => {
          setPath("product");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Provider"
        options={providerOptions}
        selectedValues={filterValue.providerIds ?? []}
        onClick={() => {
          setPath("provider");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Custody Account"
        options={custodyAccountOptions}
        selectedValues={filterValue.custodyAccountIds ?? []}
        onClick={() => {
          setPath("custody_account");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Account"
        options={assetOptions}
        selectedValues={filterValue.assetIds ?? []}
        onClick={() => {
          setPath("account");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="In flight"
        options={[{ value: "Yes", label: "Yes" }]}
        selectedValues={filterValue.inflight ? ["Yes"] : []}
        onClick={() => {
          setPath("inflight");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="External"
        options={[{ value: "Yes", label: "Exclude" }]}
        selectedValues={filterValue.excludeExternalAccounts ? ["Yes"] : []}
        onClick={() => {
          setPath("external");
          setOpen(true);
        }}
      />

      {filterCount > 0 && (
        <Button
          rightIcon={<X />}
          variant="ghost"
          size="sm"
          onClick={resetFilter}
        >
          Reset
        </Button>
      )}
    </div>
  );
}
